import React, { useState, useEffect, useRef } from 'react';
import loaderLogo from 'assets/img/loader.webp';

export default function ClientNavbar({ currentView, setCurrentView, onRefresh, isLoading = false }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const menuRef = useRef(null);
  const companyName = localStorage.getItem('companyName') || 'Client Portal';
  const userName = localStorage.getItem('name') || localStorage.getItem('username') || companyName;
  const role = localStorage.getItem('role') || 'Compliance manager';
  const email = localStorage.getItem('email') || 'client@aquasense.tech';
  const isClientAdmin = role === 'companyUser' || role === 'admin';

  // Auto-close dropdown when clicking anywhere outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get initials for avatar from user name
  const initials = userName
    ? userName
        .split(' ')
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'U';

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('district');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('companyId');
    localStorage.removeItem('companyName');
    window.location.href = '/auth/sign-in';
  };

  return (
    <header className="topnav client-topnav">
      <div className="tn-left">
        <div className="brand">
          <div className="logo">
            <img src={loaderLogo} alt="Aquasense" className="logo-desktop" style={{ width: '100%' }} />
          </div>
        </div>
      </div>

      <nav className="tn-center" id="nav">
        <a
          href="#dashboard"
          role="button"
          tabIndex={0}
          className={currentView === 'dashboard' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            setCurrentView('dashboard');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setCurrentView('dashboard');
            }
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="9" />
            <rect x="14" y="3" width="7" height="5" />
            <rect x="14" y="12" width="7" height="9" />
            <rect x="3" y="16" width="7" height="5" />
          </svg>
          <span>Dashboard</span>
        </a>

        {isClientAdmin && (
          <a
            href="#users"
            role="button"
            tabIndex={0}
            className={currentView === 'users' ? 'active' : ''}
            onClick={(e) => {
              e.preventDefault();
              setCurrentView('users');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setCurrentView('users');
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>Users</span>
          </a>
        )}

        <a
          href="#profile"
          role="button"
          tabIndex={0}
          className={currentView === 'profile' ? 'active' : ''}
          onClick={(e) => {
            e.preventDefault();
            setCurrentView('profile');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setCurrentView('profile');
            }
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0 1 16 0" />
          </svg>
          <span>Profile</span>
        </a>
      </nav>

      <div className="tn-right">
        <button
          type="button"
          className={`icon-btn ${isLoading ? 'spinning' : ''}`}
          onClick={onRefresh}
          aria-label="Refresh"
          title="Refresh telemetry"
          disabled={isLoading}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 4v6h-6M1 20v-6h6" />
            <path d="M3.5 9a9 9 0 0 1 14.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0 0 20.5 15" />
          </svg>
        </button>

        <div className={`profile ${profileOpen ? 'open' : ''}`} ref={menuRef} id="profileMenu">
          <button
            type="button"
            className="avatar-btn"
            onClick={() => setProfileOpen(!profileOpen)}
            aria-haspopup="true"
            aria-expanded={profileOpen}
          >
            <span className="avatar">{initials}</span>
            <span className="who">
              <b>{userName}</b>
              <span>{role === 'companyUser' ? 'Client Admin' : 'Compliance manager'}</span>
            </span>
            <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          <div className="dropdown">
            <div className="dd-head">
              <b>{userName}</b>
              <span>{email}</span>
            </div>
            {isClientAdmin && (
              <a
                href="#users"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentView('users');
                  setProfileOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setCurrentView('users');
                    setProfileOpen(false);
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Users</span>
              </a>
            )}
            <a
              href="#profile"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                setCurrentView('profile');
                setProfileOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCurrentView('profile');
                  setProfileOpen(false);
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21a8 8 0 0 1 16 0" />
              </svg>
              <span>Profile</span>
            </a>
            <div className="dd-sep"></div>
            <a
              href="#logout"
              role="button"
              tabIndex={0}
              className="logout"
              onClick={(e) => {
                e.preventDefault();
                handleLogout();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleLogout();
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              <span>Log out</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
